<template>
  <section v-if="activePromotions.length" class="mb-8">
    <h2 class="text-2xl font-serif font-bold text-gray-900 mb-4">
      Deals available now
    </h2>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div
        v-for="promotion in activePromotions"
        :key="promotion.id"
        class="rounded-2xl border border-primary-100 bg-white p-5 shadow-sm overflow-hidden relative"
      >
        <div
          class="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-primary-50"
        />
        <div class="relative">
          <div class="flex items-start justify-between gap-4 mb-3">
            <div>
              <h3 class="text-xl font-bold text-gray-900">
                {{ promotion.name }}
              </h3>
            </div>
            <span
              class="shrink-0 rounded-full bg-green-50 px-3.5 py-1.5 text-sm font-bold text-green-700"
            >
              Automatically applies on Cart
            </span>
          </div>

          <p class="text-sm text-gray-600 mb-3">
            {{ promotionSummary(promotion) }}
          </p>

          <p
            v-if="promotion.description"
            class="text-sm text-gray-500 line-clamp-2 mb-3"
          >
            {{ promotion.description }}
          </p>

          <div class="flex flex-wrap gap-2 text-sm">
            <span
              v-if="promotion.end_date"
              class="promotion-pill inline-flex items-center rounded-full px-3 py-1.5 font-semibold shadow-sm"
            >
              Ends {{ formatDate(promotion.end_date) }}
            </span>
            <span
              v-if="formatPromotionDays(promotion)"
              class="promotion-pill inline-flex items-center rounded-full px-3 py-1.5 font-semibold shadow-sm"
            >
              {{ formatPromotionDays(promotion) }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from "vuex";
import { getActivePromotions } from "@/services/promotionService";

/** ISO order Monday=0 … Sunday=6 (matches promotion day codes). */
const WEEKDAY_ORDER = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday"
];

const CODE_TO_INDEX = WEEKDAY_ORDER.reduce((acc, code, i) => {
  acc[code] = i;
  return acc;
}, {});

const ALL_WEEKDAY_CODES = new Set(WEEKDAY_ORDER);

const WEEKDAY_DISPLAY_NAMES = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday"
];

export default {
  name: "PromotionBanners",
  computed: {
    ...mapGetters([
      "getPromotions",
      "getStoreTimezone",
      "getServeAt",
      "orderConfig",
      "formatPrice"
    ]),
    activePromotions() {
      return getActivePromotions(this.getPromotions || [], {
        timezone: this.getStoreTimezone,
        serveAt: this.getServeAt,
        orderConfig: this.orderConfig
      });
    }
  },
  methods: {
    promotionTypeLabel(promotion) {
      return promotion.promotion_type_display || "Promotion";
    },
    promotionSummary(promotion) {
      // The backend now computes a human-readable rule_summary from rule_config.
      if (promotion.rule_summary) {
        return promotion.rule_summary;
      }

      // Fallback for promotions that may still lack rule_config
      if (promotion.promotion_type === "AOP") {
        return "Discount applies to eligible products.";
      }
      if (promotion.promotion_type === "BXY") {
        return "Buy more and save on eligible items.";
      }
      if (promotion.promotion_type === "AOO") {
        return "Discount applies to eligible orders.";
      }
      return "Promotion automatically applies when eligible.";
    },
    formatDate(dateValue) {
      const date = new Date(`${dateValue}T00:00:00`);
      if (Number.isNaN(date.getTime())) return dateValue;
      return date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric"
      });
    },
    /** Chip text: compress consecutive days (e.g. Monday thru Wednesday, …). */
    formatPromotionDays(promotion) {
      const codes = this.collectDayCodesFromPromotion(promotion);
      let indices = this.codesToSortedIndices(codes);
      if (indices.length === 0) {
        const labels = promotion?.day_of_week_display;
        if (Array.isArray(labels) && labels.length > 0) {
          const fromLabels = [...new Set(this.dayDisplayLabelsToCodes(labels))];
          indices = this.codesToSortedIndices(fromLabels);
          if (indices.length === 0) {
            return labels.join(", ");
          }
        } else {
          return "";
        }
      }
      return this.formatCompressedWeekdayIndices(indices);
    },
    collectDayCodesFromPromotion(promotion) {
      const codes = [];
      if (Array.isArray(promotion?.day_of_week)) {
        promotion.day_of_week.forEach((d) => {
          const c = String(d || "")
            .trim()
            .toLowerCase();
          if (c && ALL_WEEKDAY_CODES.has(c)) codes.push(c);
        });
      }
      let unique = [...new Set(codes)];
      if (
        unique.length === 0 &&
        Array.isArray(promotion?.day_of_week_display)
      ) {
        unique = [
          ...new Set(
            this.dayDisplayLabelsToCodes(promotion.day_of_week_display)
          )
        ];
      }
      return unique;
    },
    codesToSortedIndices(codes) {
      const indices = codes
        .map((c) => CODE_TO_INDEX[c])
        .filter((i) => i !== undefined && i !== null);
      return [...new Set(indices)].sort((a, b) => a - b);
    },
    /** Map display names like "Monday" to day codes. */
    dayDisplayLabelsToCodes(labels) {
      if (!Array.isArray(labels) || labels.length === 0) return [];
      const mapDay = (label) => {
        const key = String(label || "")
          .trim()
          .toLowerCase();
        if (!key) return null;
        if (ALL_WEEKDAY_CODES.has(key)) return key;
        for (let i = 0; i < WEEKDAY_ORDER.length; i += 1) {
          const code = WEEKDAY_ORDER[i];
          if (code.startsWith(key) || key.startsWith(code)) {
            return code;
          }
        }
        return null;
      };
      return labels.map(mapDay).filter(Boolean);
    },
    formatCompressedWeekdayIndices(sortedIndices) {
      if (sortedIndices.length === 7) {
        return "Every day";
      }

      const runs = [];
      let runStart = sortedIndices[0];
      let runEnd = sortedIndices[0];
      for (let i = 1; i < sortedIndices.length; i += 1) {
        const day = sortedIndices[i];
        if (day === runEnd + 1) {
          runEnd = day;
        } else {
          runs.push([runStart, runEnd]);
          runStart = day;
          runEnd = day;
        }
      }
      runs.push([runStart, runEnd]);

      return runs
        .map(([start, end]) => {
          if (start === end) {
            return WEEKDAY_DISPLAY_NAMES[start];
          }
          return `${WEEKDAY_DISPLAY_NAMES[start]} thru ${WEEKDAY_DISPLAY_NAMES[end]}`;
        })
        .join(", ");
    }
  }
};
</script>

<style scoped>
/* Dark orange pill: scoped so background and text always apply. */
.promotion-pill {
  background-color: #9a3412;
  color: #fff;
}
</style>
